import { type RtmBlockedReason } from '@onestep/camel-api';

import { PatientsViews } from '@/Pages/Homepage/types';
import { type SelectionRefusalKind } from '@/services/patientsService/rosterRefusal';

type IHomepageTranslations = {
  Homepage: {
    setupChat: {
      recentChats: string;
      newChat: string;
      editWithChat: string;
      staff: string;
      title: string;
      close: string;
      resize: string;
    };

    RtmPopup: {
      titleChecking: string;
      titleStart: string;
      titleStop: string;
      titleBlockedStart: string;
      titleBlockedStop: string;
      titleCheckFailed: string;
      bodyLinked: string;
      bodyBlockedSetOnly: string;
      bodyCheckFailed: string;
      setAndStart: string;
      setAndStop: string;
      setOnly: string;
      cancel: string;
      writeFailed: string;
      retry: string;
      close: string;
      stateChanged: string;
      successEnabled: string;
      successDisabled: string;
      successLeftEnabled: string;
      successLeftDisabled: string;
      reasons: Record<RtmBlockedReason, string>;
    };
    bulkAssignListLoading: string;
    SelectionError: Record<SelectionRefusalKind, { title: string; description: string }> & {
      retry: string;
      cohortFilters: { title: string; description: string };
      clearCohortFilters: string;
    };
    Views: Record<PatientsViews, string> & {
      loadFailed: string;
      workspaceOrderTitle: string;
      organizationOrderTitle: string;
      organizationOrderNote: string;
      orderOrgCardsHint: string;
      orderUpdated: string;
      orderNotSaved: string;
      orderCardsChanged: string;
      orderLockedCard: string;
      orderHiddenMark: string;
      orderHiddenHereMark: string;
      orderInstructions: string;
      orderHandleName: string;
      orderPickedUp: string;
      orderMoved: string;
      orderDropped: string;
      orderMoveCancelled: string;
      orderSave: string;
      active: string;
      cohortMarker: {
        name: string;
        tooltip: string;
      };
      hiddenCards: string;
      hideCard: string;
      rosterWriteForbidden: string;
      rosterWriteNeedsWorkspace: string;
      dependentBreak: string;
      noVisibleCards: string;
      showHiddenCard: string;
      saveCard: {
        baseChanged: string;
        confirmTitle: string;
        organizationMessage: string;
        savedToast: string;
        failedToast: string;
      };
      systemCardUpdateFailed: string;
      newViewWithAgent: string;
      unavailableViewNotice: string;
      clearedWorkspaceFilters: string;
      deleteCard: {
        savedView: {
          title: string;
          workspaceMessage: string;
          workspaceMessage_cohorts: string;
          organizationEveryWorkspaceMessage: string;
          organizationEveryWorkspaceMessage_cohorts: string;
          confirm: string;
        };
      };
      cohortChat: {
        viewNotEditable: string;
        pickCohortTitle: string;
        pickCohortConfirm: string;
        pickCohortCancel: string;
        showingOnRoster: string;
      };
    };
  } & Record<string, unknown>;
};

export const enHomepageTranslations: IHomepageTranslations = {
  Homepage: {
    setupChat: {
      recentChats: 'Recent chats',
      newChat: 'New chat',
      editWithChat: 'Edit with chat',
      staff: 'Staff',
      title: 'Setup chat',
      close: 'Close setup chat',
      resize: 'Resize setup chat',
    },

    ViewForm: {
      showInApp: 'Show in mobile app',
      draftBlocked: 'Publish your draft cohorts before saving a view.',
      clearDraftPicks: 'Clear draft picks',
      unavailablePicks:
        'Some cohorts you picked are no longer available. Clear them before saving a view.',
      clearUnavailablePicks: 'Clear unavailable picks',
      unavailableCohorts:
        'These cohorts are no longer available: {{names}}. Clear their picks before saving a view.',
      unavailableStoredCohorts:
        '{{names}} no longer exist. Save a new view without them, or delete this view.',
      catalogFailed: "Couldn't load cohorts. Try again before saving a view.",
      notAllowed: 'You are not allowed to edit this view',
      createForbidden: 'You don’t have permission to create views here',
      moveToWorkspaceTitle: 'Move "{{name}}" to this workspace only?',
      moveToWorkspaceMessage: '{{name}} will leave every other workspace.',
      workspaceHint:
        '{{names}} belongs to {{workspace}}, so a view that uses it can only belong to {{workspace}}.',
      parameterizedFilterLocked:
        "This filter has settings the portal can't change. Ask the setup chat to update it.",
      editHint:
        'Edit view changes how it looks. To change what it matches, edit the cohort, or save a new view from the roster and delete this one.',
      nameConflict: 'The {{object}} “{{name}}” in the {{scope}} already uses this name.',
      workspace: 'workspace',
      organization: 'organization',
      scopeMismatch: 'A view using workspace cohorts must belong to that workspace.',
      limit: 'A view can combine at most 10 cohorts. Remove a pick before saving.',
      conflict:
        'A cohort cannot be included and excluded in the same view. Change your picks before saving.',
      immutable: 'Save a new view to change its rule.',
      dependentBreak: 'This change would break other things that use it.',
      memberUnavailable: 'A cohort in this view is no longer available.',
      referenceMalformed: 'A cohort reference here isn’t written correctly.',
      referenceUnavailable: 'A cohort used here isn’t available.',
      referenceSuperseded: 'A cohort used here has a newer version. Use the newer one.',
      referenceDynamic: 'A cohort reference here can’t depend on a value that changes.',
      referenceCycle: 'These cohorts refer to each other in a loop, so this can’t be saved.',
      referenceDepth: 'These cohorts are nested too deeply to be saved.',
      argumentsInvalid: 'The values given to a cohort here aren’t valid.',
      parametersImmutable: 'The settings of a saved cohort can’t be changed in place.',
      parametersUnsupported: 'A cohort with settings can’t be edited here. Use the setup chat.',
      slugReserved: 'This name is reserved. Pick another name.',
      measurementStale:
        'The definition changed or has not replicated. Preview it again before publishing.',
      builtinUnavailable:
        'This view starts from a patient group this organization doesn’t offer, so it can’t be saved.',
      failed: 'Couldn’t save the view. Your entries are kept; try again.',
    },
    CohortFilters: {
      manageCohorts: 'Manage cohorts',
      openDraft: 'Open draft',
      draftLabel: '{{name}} (Draft)',
      unavailablePicks: 'Some filters you picked are no longer available.',
      droppedPicks_one: '1 filter you picked is no longer available and was removed.',
      droppedPicks_other: '{{count}} filters you picked are no longer available and were removed.',
      removeUnavailablePicks: 'Remove',
      builtIn: 'Built-in',
      organizationCategories: 'Organization categories',
      workspaceCategories: '{{workspace}} categories',
      description:
        'Shows patients in any cohort you pick in a category. Picks in different categories must all match.',
      noneOf: 'None of these',
      noneOfPublished: 'None of the published cohorts',
      savedInThisView: 'Saved in this view',
      includes: 'Includes: {{names}}',
      excludes: 'Excludes: {{names}}',
      savedIncludeInCategory: 'Includes ({{category}}): {{names}}',
      savedExcludeInCategory: 'Excludes ({{category}}): {{names}}',
      replacedByPick: 'replaced by your pick',
      checkingPicks: 'Checking your picks…',
      activePatients: 'Includes: active patients',
      allPatientsNoEpisodeOfCare: 'Includes: all patients',
      allPatients: 'Includes: all patients, including discharged',
      savedSection: '{{section}}: {{selection}}',
      limit:
        'A selection can combine at most 10 cohorts, including the view’s saved rule. Remove a pick to apply.',
      conflict:
        'A cohort cannot be included and excluded in the same selection. Change your picks to apply.',
      clearNotice: "Clear filters removes only the picks you added. The view's saved rule stays.",
      loadFailed: 'Could not load cohort categories.',
      retry: 'Try again',
    },
    RtmPopup: {
      titleChecking: 'Checking RTM for {{patientDisplayName}}',
      titleStart: 'Start RTM for {{patientDisplayName}}?',
      titleStop: 'Stop RTM for {{patientDisplayName}}?',
      titleBlockedStart: "RTM can't start today",
      titleBlockedStop: "RTM can't stop today",
      titleCheckFailed: "Couldn't check RTM",
      bodyLinked: '{{optionLabel}} is linked to RTM.',
      bodyBlockedSetOnly: 'You can still set {{attributeLabel}} to {{optionLabel}}.',
      bodyCheckFailed: "{{attributeLabel}} isn't set yet.",
      setAndStart: 'Set and start RTM',
      setAndStop: 'Set and stop RTM',
      setOnly: 'Set {{attributeLabel}} only',
      cancel: 'Cancel',
      writeFailed: "Couldn't update the field and RTM setting. Please try again.",
      retry: 'Retry',
      close: 'Close',
      stateChanged: 'RTM changed while this was open.',
      successEnabled: 'Set {{attributeLabel}} to {{optionLabel}} and enabled RTM',
      successDisabled: 'Set {{attributeLabel}} to {{optionLabel}} and disabled RTM',
      successLeftEnabled: 'Set {{attributeLabel}} to {{optionLabel}} and left RTM enabled',
      successLeftDisabled: 'Set {{attributeLabel}} to {{optionLabel}} and left RTM disabled',
      reasons: {
        rtm_off_for_workspace: 'RTM is off for this workspace.',
        overlap: 'Another RTM billing period overlaps today.',
        period_started_today: "The RTM period started today and can't be ended today.",
        period_ended_today: 'The previous RTM period ended today; a new one can start tomorrow.',
      },
    },
    bulkAssignListLoading:
      'The patient list is still loading. Wait for it to finish, then try again.',
    SelectionError: {
      'view-unavailable': {
        title: "This view isn't available",
        description: 'It may have been deleted or moved. Choose another view or {{card}}.',
      },
      'cohort-unavailable': {
        title: "A cohort in this selection isn't available",
        description:
          "It may have been removed or isn't visible in this workspace. Choose another view.",
      },
      'filter-unavailable': {
        title: "This filter can't be applied",
        description: 'Choose another card or change the filter.',
      },
      'too-many-cohorts': {
        title: 'This selection uses too many cohorts',
        description: 'A selection can combine at most 10 cohorts. Choose a narrower view.',
      },
      'cohort-conflict': {
        title: 'This selection includes and excludes the same cohort',
        description: 'Change the cohort selection or choose another view.',
      },
      cohortFilters: {
        title: 'Could not apply your cohort filters',
        description:
          'Some cohort filters are unavailable or conflict with this selection. Remove them and try again.',
      },
      clearCohortFilters: 'Clear cohort filters',
      'timed-out': {
        title: 'The list took too long to load',
        description: 'Try again. If it keeps happening, choose a narrower view.',
      },
      failed: {
        title: "Couldn't load patients",
        description: 'Something went wrong. Try again.',
      },
      retry: 'Try again',
    },
    EmptyStates: {
      [PatientsViews.All]: {
        title: 'no patients in this workspace',
        description:
          'This could mean no patients have been added yet, or there may be a data sync delay.',
      },
      noPatients: 'no patients fit the current filters',
      noPatientsDescription:
        'Your current filters may be too specific—modify or clear them to view more patients. You can also add a new patient below.',
    },
    RecentDataFilter: {
      title: 'Recent data',
      recent: 'Up to date',
      none: 'No data',
    },
    Header: {
      welcomeMsg: `Welcome, let’s review your patients`,
      resetChanges: 'Reset changes',
      Action: {
        addNewPatient: 'Add a patient',
        viewPatient: 'View patient',
        discardTitle: 'Discard patient details?',
        discardBody:
          'This patient hasn’t been created yet. If you leave now, the information you entered will be lost.',
        discardAndClose: 'Discard & close',
        keepEditing: 'Keep editing',
      },
      Modal: {
        generateRollup: 'Generate rollup',
        generateRollupTitle: 'Generate Rollup report',
        downloadRollup: 'Download Rollup',
        showOnlyDecliningPatients: 'Show only declining patients',
        showUnassignedPatients: 'Show unassigned patients',
        generatingRollupReport: 'Generating Rollup report',
        tocFilter: 'Filter patients based on type of care',
      },
    },
    Views: {
      hiddenCards: 'Hidden cards ({{count}})',
      hideCard: 'Hide',
      saveCard: {
        baseChanged: 'The base cohort cannot be changed by Save. Use Edit or save a new view.',
        confirmTitle: 'Save "{{name}}"?',
        organizationMessage:
          'This change applies to the whole organization. Every workspace that shows "{{name}}" will see it.',
        savedToast: 'Card saved.',
        failedToast: "The card wasn't saved. Try again.",
      },
      rosterWriteForbidden: 'You don’t have permission to update roster cards in this workspace.',
      rosterWriteNeedsWorkspace: 'Select a workspace to update roster cards.',
      dependentBreak: 'This change would break other things that use it.',
      noVisibleCards: 'No visible cards',
      showHiddenCard: '{{label}} — Show',
      systemCardUpdateFailed: 'Could not update the roster card. Please try again.',
      loadFailed: "Couldn't load views",
      workspaceOrderTitle: 'Reorder cards',
      organizationOrderTitle: 'Reorder organization cards',
      organizationOrderNote: 'Applies to every workspace.',
      orderOrgCardsHint: 'To move organization cards, choose Reorder on one of them.',
      orderUpdated: 'Order updated.',
      orderNotSaved: "The order wasn't saved. Nothing was changed. Try again.",
      orderCardsChanged:
        "The cards changed while this was open. This is the current order. Your changes weren't saved.",
      orderLockedCard: '{{label}}, organization card, locked',
      orderHiddenMark: 'Hidden',
      orderHiddenHereMark: 'Hidden in this workspace',
      orderInstructions:
        'Press Space or Enter to pick up a card. Use the arrow keys to move it, Space or Enter to drop it, and Escape to cancel.',
      orderHandleName: '{{label}}, position {{n}} of {{total}}',
      orderPickedUp:
        '{{label}} picked up, position {{n}} of {{total}}. Use the arrow keys to move, Space to drop, Escape to cancel.',
      orderMoved: '{{label}} moved to position {{n}} of {{total}}.',
      orderDropped: '{{label}} dropped at position {{n}} of {{total}}.',
      orderMoveCancelled: 'Move cancelled. {{label}} is back at position {{n}}.',
      orderSave: 'Save',
      [PatientsViews.All]: 'All patients',
      active: 'Active',
      cohortMarker: {
        name: 'Cohort',
        tooltip: 'Cohort, built with the agent',
      },
      newViewWithAgent: 'New view with agent',
      unavailableViewNotice: 'View not available in this workspace',
      clearedWorkspaceFilters: 'Cleared filters from the previous workspace’s categories',
      deleteCard: {
        savedView: {
          title: 'Delete View',
          workspaceMessage:
            'Are you sure you want to delete {{name}}? The view will be removed from your workspace views.',
          workspaceMessage_cohorts:
            'Are you sure you want to delete {{name}}? The view will be removed from your workspace views. The cohorts it uses stay available.',
          organizationEveryWorkspaceMessage:
            'Are you sure you want to delete {{name}}? This card will be removed from every workspace in your organization.',
          organizationEveryWorkspaceMessage_cohorts:
            'Are you sure you want to delete {{name}}? This card will be removed from every workspace in your organization. The cohorts it uses stay available.',
          confirm: 'Delete',
        },
      },
      cohortChat: {
        viewNotEditable: 'This view has no cohort the agent can edit.',
        pickCohortTitle: 'Which cohort should the agent edit?',
        pickCohortConfirm: 'Edit',
        pickCohortCancel: 'Cancel',
        showingOnRoster: 'Showing on the roster',
      },
    },
  },
};
